import { useMemo } from "react";
import { LegendKey } from "../../../../models/chart_model";
import { getColor, getDistinctColorsForKeys } from "../../../../utility/Colors";
import { compareLegends } from "../utils/legendUtils";
import { getLegendColorKey } from "../utils/colorUtils";

/**
 * Bundles 3 chained derivations:
 *  - filteredLegends: legends excluding xDataKey
 *  - colorMap: stable color per legend key (cached on key signature)
 *  - orderedSelectedLegends: selectedLegends sorted via compareLegends
 */
export function useLegendDerivations(
  legends: LegendKey[],
  selectedLegends: LegendKey[],
  xDataKey: string,
  customLegendOrder?: string[],
) {
  const filteredLegends = useMemo(
    () => legends.filter((e) => e.dataKey !== xDataKey),
    [legends, xDataKey],
  );

  // Cache key — stable string of color keys. Avoids recomputing
  // getDistinctColorsForKeys when legend identities are unchanged but
  // array reference flipped.
  const colorKeySignature = useMemo(
    () =>
      filteredLegends
        .map((l, i) => getLegendColorKey(l, i))
        .join("|"),
    [filteredLegends],
  );

  const colorMap = useMemo(() => {
    const legendColorKeys = filteredLegends.map((legend, index) =>
      getLegendColorKey(legend, index),
    );
    const distinctColors = getDistinctColorsForKeys(
      legendColorKeys,
    ) as Record<string, string>;
    const map: Record<string, string> = {};
    filteredLegends.forEach((legend, index) => {
      const legendColorKey = getLegendColorKey(legend, index);
      map[legendColorKey] =
        distinctColors[legendColorKey] ?? getColor(index, legendColorKey);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorKeySignature]);

  const orderedSelectedLegends = useMemo(() => {
    const base =
      selectedLegends.length === 0
        ? filteredLegends
        : legends.filter((legend) =>
            selectedLegends.some(
              (selected) => selected.dataKey === legend.dataKey,
            ),
          );
    return [...base].sort((a, b) =>
      compareLegends(a.name || "", b.name || "", customLegendOrder),
    );
  }, [selectedLegends, filteredLegends, legends, customLegendOrder]);

  return { filteredLegends, colorMap, orderedSelectedLegends };
}
